import {
  Card,
  Spin,
  Statistic,
  message,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { searchStatsDeleteUserReport } from 'src/api/reports'

import { SearchOutlined } from '@ant-design/icons'
import {
  GENDER_TYPE,
  mncmccOptions,
  PHONE_INPUT,
  userType,
} from 'src/constants'
import { fetchCountryProvider } from 'src/api/authen'
import { UserNameAutoComplete } from 'src/components/Search/UserNameSearch'
import moment from 'moment'

interface UserDeleteAccountSearchProps {
  appendSearchForm: (obj: any) => void
  submitSearchForm: () => void
}

interface Game {
  id: string
  title: string
}

const UserDeleteAccountSearch: React.FC<UserDeleteAccountSearchProps> = ({
  appendSearchForm,
  submitSearchForm,
}) => {
  const [country, setCountry] = useState('ALL')
  const [countries, setCountries] = useState([])
  const [buttonAction, setButtonAction] = useState('Select')
  const initialStartDate = moment().subtract(7, 'days').startOf('day')
  const initialEndDate = moment().endOf('day')
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [selectedFilterValue, setSelectedFilterValue] = useState('ALL')
  const [metricsData, setMetricsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [criteria, setCriteria] = useState<any>({})

  const updateCriteria = (obj: any) => {
    setCriteria((prev) => ({ ...prev, ...obj }))
    appendSearchForm(obj)
  }

  const computeMetrics = async (filters: any) => {
    try {
      const response = await searchStatsDeleteUserReport({
        ...filters,
        page: 0,
        pageSize: 1,
      })
      const totalUsers = response.deleteUsers.total_items
      const deviceTotal = response.iosCount + response.androidCount
      const genderTotal = response.maleCount + response.femaleCount
      return {
        totalUsers,
        iosDevicePercentage:
          deviceTotal > 0 ? (response.iosCount / deviceTotal) * 100 : 0,
        androidDevicePercentage:
          deviceTotal > 0 ? (response.androidCount / deviceTotal) * 100 : 0,
        malePercentage:
          genderTotal > 0 ? (response.maleCount / genderTotal) * 100 : 0,
        femalePercentage:
          genderTotal > 0 ? (response.femaleCount / genderTotal) * 100 : 0,
      }
    } catch (err) {
      console.error('Error computing metrics', err)
      return {
        totalUsers: 0,
        iosDevicePercentage: 0,
        androidDevicePercentage: 0,
        malePercentage: 0,
        femalePercentage: 0,
      }
    }
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = data.data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }))
      countryOptions.unshift({ label: 'All', value: 'ALL' })
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleSearchClick = () => {
    updateCriteria({ buttonAction: ' ' })
    submitSearchForm()
    setButtonAction(null)
  }
  const allUserTypeOptions = [{ label: 'All', value: 'ALL' }, ...userType]

  const handleFilterChange = (value) => {
    setSelectedFilterValue(value)
    let userTypesForSearch
    if (value === 'ALL') {
      userTypesForSearch = userType.map((option) => option.value)
    } else {
      userTypesForSearch = [value]
    }
    updateCriteria({ userAccessType: userTypesForSearch })
  }

  const handleCountryChange = (value) => {
    setCountry(value)
    if (value === 'ALL') {
      updateCriteria({ providerCountry: undefined })
    } else {
      updateCriteria({ providerCountry: value })
    }
  }

  useEffect(() => {
    fetchCountry()
    updateCriteria({ startDate: initialStartDate.format('YYYY-MM-DD') })
    updateCriteria({ endDate: initialEndDate.format('YYYY-MM-DD') })
    submitSearchForm()
  }, [])
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true)
      const metrics = await computeMetrics(criteria)
      setMetricsData(metrics)
      setLoading(false)
    }
    fetchMetrics()
  }, [criteria])

  useEffect(() => {
    if (Object.keys(criteria).length > 0) {
      submitSearchForm()
    }
  }, [criteria])

  return (
    <div className="master-search-wrap">
      <Row gutter={15}>
        <Col span={4}>
          <UserNameAutoComplete appendSearchForm={appendSearchForm} />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Select User Type"
            onChange={handleFilterChange}
            onClear={() => {
              updateCriteria({ userAccessType: '' })
            }}
            className="search-input"
          >
            {allUserTypeOptions.map(({ label, value }) => (
              <Select.Option value={value} key={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Col>

        <Col span={8}>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
            value={[startDate, endDate]}
            onChange={(dates) => {
              const newStart = dates?.[0] || null
              const newEnd = dates?.[1] || null
              setStartDate(newStart)
              setEndDate(newEnd)
              updateCriteria({
                startDate: newStart ? newStart.format('YYYY-MM-DD') : '',
                endDate: newEnd ? newEnd.format('YYYY-MM-DD') : '',
              })
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Select Country"
            value={country}
            onChange={handleCountryChange}
            options={countries}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Select Gender"
            onChange={(value) => updateCriteria({ gender: value })}
            allowClear
            onClear={() => updateCriteria({ gender: '' })}
            className="search-input"
          >
            {GENDER_TYPE.map((val) => (
              <Select.Option value={val} key={val}>
                {val}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={15}>
        <Col span={4}>
          <Select
            placeholder="Select Device Type"
            onChange={(value) => updateCriteria({ source: value })}
            onClear={() => updateCriteria({ source: '' })}
            allowClear
            className="search-input"
          >
            {PHONE_INPUT.map(({ label, value }) => (
              <Select.Option value={value} key={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            placeholder="Source "
            onChange={(value) => {
              updateCriteria({ mncmcc: value })
              if (!value) submitSearchForm()
            }}
            allowClear
            onClear={() => {
              updateCriteria({ mncmcc: '' })
              submitSearchForm()
            }}
            showSearch
            className="search-input"
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.label?.toString().toLowerCase() || '').includes(
                input.toLowerCase()
              )
            }
          >
            {mncmccOptions.map(({ value, label }) => (
              <Select.Option value={value} key={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearchClick}
            className="btn-primary"
            size="large"
          >
            Search
          </Button>
        </Col>
      </Row>

      <Row gutter={15} style={{ display: 'flex' }}>
        <Col span={8} style={{ display: 'flex' }}>
          <Card
            style={{
              borderRadius: '10px',
              padding: '1rem',
              color: 'white',
              flex: 1,
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <Spin spinning={loading || !metricsData}>
              <Statistic
                title={
                  <span style={{ color: '#ccc', fontSize: '14px' }}>
                    Total Deleted Users
                  </span>
                }
                valueRender={() => (
                  <span
                    style={{
                      color: 'rgb(92, 110, 242)',
                      fontSize: '32px',
                      fontWeight: 700,
                    }}
                  >
                    {metricsData?.totalUsers || 0}
                  </span>
                )}
              />
            </Spin>
          </Card>
        </Col>

        <Col span={8} style={{ display: 'flex' }}>
          <Card
            style={{
              borderRadius: '10px',
              padding: '1rem',
              color: 'white',
              flex: 1,
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <Spin spinning={loading || !metricsData}>
              <div
                style={{
                  marginBottom: '0.1rem',
                  fontSize: '14px',
                  color: '#ccc',
                }}
              >
                Delete Account By Device
              </div>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div>
                  <div
                    style={{
                      fontSize: '22px',
                      color: 'rgb(92, 110, 242)',
                      fontWeight: 600,
                    }}
                  >
                    {(metricsData?.androidDevicePercentage || 0).toFixed(2)}%
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#fff',
                      marginTop: '0.05rem',
                    }}
                  >
                    Android
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: '22px',
                      color: 'rgb(92, 110, 242)',
                      fontWeight: 600,
                    }}
                  >
                    {(metricsData?.iosDevicePercentage || 0).toFixed(2)}%
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#fff',
                      marginTop: '0.05rem',
                    }}
                  >
                    IOS
                  </div>
                </div>
              </div>
            </Spin>
          </Card>
        </Col>

        <Col span={8} style={{ display: 'flex' }}>
          <Card
            style={{
              borderRadius: '10px',
              padding: '1rem',
              color: 'white',
              flex: 1,
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <Spin spinning={loading || !metricsData}>
              <div
                style={{
                  marginBottom: '0.1rem',
                  fontSize: '14px',
                  color: '#ccc',
                }}
              >
                Delete Account By Gender
              </div>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div>
                  <div
                    style={{
                      fontSize: '22px',
                      color: 'rgb(92, 110, 242)',
                      fontWeight: 600,
                    }}
                  >
                    {(metricsData?.malePercentage || 0).toFixed(2)}%
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#fff',
                      marginTop: '0.05rem',
                    }}
                  >
                    Male
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: '22px',
                      color: 'rgb(92, 110, 242)',
                      fontWeight: 600,
                    }}
                  >
                    {(metricsData?.femalePercentage || 0).toFixed(2)}%
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#fff',
                      marginTop: '0.05rem',
                    }}
                  >
                    Female
                  </div>
                </div>
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

UserDeleteAccountSearch.displayName = 'UserDeleteAccountSearch'
export default UserDeleteAccountSearch
